import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Arrange,
  Container,
  FormField,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Pill,
  SvgClose,
  Text,
} from '@loomhq/lens';
import classnames from 'classnames';
import Downshift from 'downshift';
import pluralize from 'pluralize';
import React, { useEffect, useRef, useState } from 'react';
import { useLayer } from 'react-laag';
import ResizeObserver from 'resize-observer-polyfill';

import { LOOM_URI } from 'constants/routes';

import AddTagToVideoMutation from './AddTagToVideoMutation.graphql';
import GetTagsByVideoIdQuery from './GetTagsByVideoIdQuery.graphql';
import RemoveTagFromVideoMutation from './RemoveTagFromVideoMutation.graphql';
import SearchWorkspaceTags from './SearchWorkspaceTags.graphql';
import { INVALID_TAG_CHAR_REGEX } from '../../../../../shared/constants/workspaceTags';

import styles from './styles.module.less';

const MAX_TAGS = 10;
const MAX_TAG_LENGTH = 20;

const formatOption = (item, input) => {
  const { count, tag } = item;

  const indexStart = tag.indexOf(input);
  const indexEnd = indexStart + input.length;

  const content =
    indexStart < 0 ? (
      tag
    ) : (
      <>
        {tag.substring(0, indexStart)}
        <span className="weight:medium">
          {tag.substring(indexStart, indexEnd)}
        </span>
        {tag.substring(indexEnd)}
      </>
    );

  return (
    <>
      <Text>
        <span className="weight:medium">{`#`}</span>
        {content}
      </Text>
      <Text color="bodyDimmed">
        {count} {pluralize('videos', count)}
      </Text>
    </>
  );
};

const formatNewTag = input => (
  <>
    <Text>
      <span className="weight:medium">
        {`#`}
        {input.toLocaleLowerCase()}
      </span>
    </Text>
    <Text color="bodyDimmed">Create new tag</Text>
  </>
);

const sanitizeText = input => {
  const trimmed = input.trim().toLocaleLowerCase();

  return trimmed.replace(INVALID_TAG_CHAR_REGEX, '');
};

const TagsTypeahead = ({ initialTags, videoId }) => {
  const [selectedItems, setSelectedItems] = useState(initialTags);
  const [options, setOptions] = useState([]);
  const [hasOptions, setHasOptions] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const [downshiftIsOpen, setDownshiftIsOpen] = useState(false);

  const [runQuery] = useLazyQuery(SearchWorkspaceTags, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const selectedItemsSet = new Set(selectedItems);

      const typeaheadOptions = data.result.tags.filter(
        option => !selectedItemsSet.has(option.tag)
      );

      setOptions(typeaheadOptions);
      setHasOptions(true);
    },
  });

  useEffect(() => {
    if (!inputValue) {
      setOptions([]);
      setDownshiftIsOpen(false);
      setHasOptions(false);
    }

    if (inputValue?.length > 0) {
      runQuery({
        variables: { query: inputValue },
      });

      if (errorMessage) {
        setErrorMessage('');
      }
    }
  }, [inputValue, errorMessage, runQuery]);

  const [removeTagFromVideo] = useMutation(RemoveTagFromVideoMutation, {
    onCompleted: data => {
      if (data.result?.tag) {
        setSelectedItems(selectedItems.filter(i => i !== data.result.tag));
      }
    },
  });

  const removeTag = tag => {
    removeTagFromVideo({
      variables: {
        videoId,
        tag,
        context: 'Share page',
      },
    });

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const [addTagToVideo] = useMutation(AddTagToVideoMutation, {
    onCompleted: data => {
      if (data.result?.tag) {
        setSelectedItems([...selectedItems, data.result?.tag]);
      }
    },
  });

  const addTag = input => {
    const tag = input?.tag ? input.tag : input;
    let error;

    if (selectedItems.length >= MAX_TAGS) {
      error = 'A maximum of 10 tags can be associated with each video.';
    }

    const sanitized = sanitizeText(tag);

    if (selectedItems.includes(sanitized)) {
      error = `#${sanitized} already exists.`;
    }

    if (error) {
      setErrorMessage(error);
    } else {
      addTagToVideo({
        variables: {
          videoId,
          tag,
          context: 'Share page',
        },
      });
    }

    setInputValue('');
    inputRef.current.focus();
  };

  const onKeyDown = e => {
    if (e.key === 'Backspace' && !inputValue && selectedItems.length > 0) {
      removeTag(selectedItems[selectedItems.length - 1]);
    }

    if ((e.key === 'Spacebar' || e.key === ' ') && inputValue) {
      addTag(inputValue);
    }
  };

  const stateReducer = (state, changes) => {
    if (changes.isOpen !== undefined) {
      setDownshiftIsOpen(changes.isOpen);
    }

    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
      case Downshift.stateChangeTypes.keyDownSpaceButton: {
        addTag(changes.selectedItem);

        return {
          ...changes,
          highlightedIndex: state.highlightedIndex,
          isOpen: false,
          inputValue: '',
        };
      }
      case Downshift.stateChangeTypes.changeInput: {
        const sanitized = sanitizeText(changes.inputValue);

        if (sanitized.length === 0) {
          return {
            ...changes,
            isOpen: false,
          };
        }

        return changes;
      }
      default:
        return changes;
    }
  };

  const { layerProps, triggerProps, renderLayer, triggerBounds } = useLayer({
    isOpen: downshiftIsOpen,
    ResizeObserver,
    placement: 'bottom-start',
    auto: true,
    snap: true,
  });

  return (
    <Downshift
      itemToString={item => (item ? item.tag : '')}
      stateReducer={stateReducer}
      defaultHighlightedIndex={0}
    >
      {({
        getInputProps,
        getMenuProps,
        isOpen,
        getItemProps,
        highlightedIndex,
      }) => (
        <div>
          <div {...triggerProps}>
            <FormField
              labelFor="tags"
              errorMessage={errorMessage}
              onClick={() => !isOpen && inputRef.current.focus()}
            >
              <Container
                paddingTop="small"
                paddingX="medium"
                radius="medium"
                borderColor={isOpen && 'primary'}
                className={`${styles.tagsList} flex flexWrap items:center ${
                  inputFocused ? styles.tagsListActive : ''
                }`}
              >
                {selectedItems.map((selectedItem, index) => (
                  <Pill
                    key={index}
                    className={`${styles.pillCreator} mr:xsmall mb:small`}
                    backgroundColor="blurpleLight"
                  >
                    <Arrange gap="xsmall">
                      <Link
                        variant="neutral"
                        href={`${LOOM_URI}/tag/${selectedItem}`}
                        className={styles.tagLink}
                      >
                        {`#${selectedItem}`}
                      </Link>
                      <IconButton
                        altText="Close"
                        icon={<SvgClose />}
                        size="small"
                        onClick={() => removeTag(selectedItem)}
                      />
                    </Arrange>
                  </Pill>
                ))}
                <Pill
                  className={`mr:xsmall mb:small ${styles.inputPill}`}
                  color="bodyDimmed"
                >
                  <Arrange>
                    <Text color={inputFocused ? 'body' : 'bodyDimmed'}>
                      {`#`}
                    </Text>
                    <input
                      {...getInputProps({
                        ref: inputRef,
                        maxLength: MAX_TAG_LENGTH,
                        onKeyDown,
                        onChange: ({ target }) =>
                          setInputValue(sanitizeText(target.value)),
                        onFocus: () => setInputFocused(true),
                        onBlur: () => {
                          setInputFocused(false);
                          setInputValue('');
                        },
                      })}
                      placeholder="add tag"
                      className={styles.input}
                      value={inputValue}
                      size={6}
                    />
                  </Arrange>
                </Pill>
              </Container>
            </FormField>
          </div>
          {isOpen &&
            downshiftIsOpen &&
            !errorMessage &&
            renderLayer(
              <div
                {...layerProps}
                style={{
                  ...layerProps.style,
                  width: triggerBounds.width,
                  zIndex: 1100,
                }}
              >
                {hasOptions && (
                  <Menu downshiftMenuProps={getMenuProps}>
                    {options.length > 0 ? (
                      <>
                        {options.map((item, index) => (
                          <MenuItem
                            key={index}
                            className={classnames(
                              'pl:medium',
                              highlightedIndex === index &&
                                'bgc:backgroundHover'
                            )}
                            {...getItemProps({
                              item,
                              index,
                              isSelected: selectedItems.includes(item),
                            })}
                          >
                            {formatOption(item, inputValue)}
                          </MenuItem>
                        ))}
                      </>
                    ) : (
                      <MenuItem
                        className={classnames(
                          'pl:medium',
                          highlightedIndex === 0 && 'bgc:backgroundHover'
                        )}
                        {...getItemProps({
                          index: 0,
                          item: inputValue,
                          isSelected: selectedItems.includes(inputValue),
                        })}
                      >
                        {formatNewTag(inputValue)}
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </div>
            )}
        </div>
      )}
    </Downshift>
  );
};

const TagsSection = ({ videoId, canEdit }) => {
  const { loading, data } = useQuery(GetTagsByVideoIdQuery, {
    variables: { videoId },
  });

  const tags = data?.result?.tags;

  if (loading) {
    return null;
  }

  if (canEdit && tags) {
    return (
      <div className={`${styles.tagWrapper} my:small`}>
        <TagsTypeahead initialTags={tags} videoId={videoId} />
      </div>
    );
  }

  return (
    <div className={`${styles.tagWrapper} my:small`}>
      {tags?.map(tag => (
        <Pill
          key={tag}
          className={`${styles.pillViewer} m:xsmall`}
          backgroundColor="blurpleLight"
        >
          <Link
            variant="neutral"
            href={`${LOOM_URI}/tag/${tag}`}
            className={styles.tagLink}
          >
            {`#${tag}`}
          </Link>
        </Pill>
      ))}
    </div>
  );
};

export default TagsSection;
